import { Check, Sparkles, Split, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  useAcceptBankIncomingSuggestion,
  useRejectBankIncomingSuggestion,
} from '@/hooks/react-query/useBankIncoming';
import type { BankIncomingScope } from '@/services/bankIncomingService';
import {
  MATCH_BASIS_LABELS,
  type BankIncomingAdminLine,
  type IncomingMatchStatus,
} from '@/types/bank-reconciliation';
import { formatCurrency } from '@/utils/formatters';
import { sameToTheCent } from '@/utils/number-operations';

export function SuggestionBadge({ line }: { line: BankIncomingAdminLine }) {
  if (!line.suggestion_basis) return null;
  return (
    <Badge variant="outline" className="gap-1 whitespace-nowrap">
      <Sparkles className="h-3 w-3" />
      {MATCH_BASIS_LABELS[line.suggestion_basis]}
    </Badge>
  );
}

export function SuggestionText({ line }: { line: BankIncomingAdminLine }) {
  if (!line.suggestion_note) {
    return <span className="text-sm text-muted-foreground">No suggestion</span>;
  }
  return (
    <p className="text-sm text-foreground leading-snug">
      {line.suggestion_note}
    </p>
  );
}

/**
 * What an operator can do with a suggestion the matcher left on a line.
 *
 * Three choices, and they are only legible if they look different from each
 * other: agree with the proposal, decide the amounts yourself, or say the
 * proposal is wrong. The one the operator is most likely to want is the filled
 * button; the way out is a quiet one, and it says what it does rather than
 * leaving a bare cross to be guessed at.
 */
export function SuggestionActions({
  line,
  onAllocate,
  scope,
}: {
  line: BankIncomingAdminLine;
  /** Opens the allocation screen for this line. */
  onAllocate?: (line: BankIncomingAdminLine) => void;
  /** Omitted in the admin module; set to the fund when shown inside a fund. */
  scope?: BankIncomingScope;
}) {
  const accept = useAcceptBankIncomingSuggestion(scope);
  const reject = useRejectBankIncomingSuggestion(scope);

  if (!PLACEABLE.includes(line.match_status) || !line.suggestion_basis) {
    return null;
  }

  const busy = accept.isPending || reject.isPending;
  // A suggestion covering several open items names no single transaction, so
  // there is nothing for one click to book: the screen that can place them all
  // becomes the main way forward instead.
  const acceptable =
    line.suggested_fund_transaction_id !== null ||
    line.suggestion_basis === 'FUND_MANAGER_IBAN';
  const currency = line.currency ?? undefined;
  const takes = suggestedAmount(line);
  const leftover =
    takes !== null && !sameToTheCent(takes, line.unapplied_amount)
      ? line.unapplied_amount - takes
      : 0;

  const acceptTitle =
    leftover > 0
      ? `Accept — books ${formatCurrency(takes as number, { currency })} of this payment`
      : 'Accept — books what the matcher proposed';

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1">
        {acceptable && (
          <Button
            size="icon"
            className="h-8 w-8"
            disabled={busy}
            title={acceptTitle}
            aria-label={acceptTitle}
            onClick={(event) => {
              event.stopPropagation();
              accept.mutate(line.id);
            }}
          >
            <Check className="h-4 w-4" />
          </Button>
        )}

        {onAllocate && (
          <Button
            size="icon"
            variant={acceptable ? 'ghost' : 'default'}
            className="h-8 w-8"
            disabled={busy}
            title={ALLOCATE_TITLE}
            aria-label={ALLOCATE_TITLE}
            onClick={(event) => {
              event.stopPropagation();
              onAllocate(line);
            }}
          >
            <Split className="h-4 w-4" />
          </Button>
        )}

        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 text-muted-foreground"
          disabled={busy}
          title={REJECT_TITLE}
          aria-label={REJECT_TITLE}
          onClick={(event) => {
            event.stopPropagation();
            reject.mutate({ incomingId: line.id });
          }}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {leftover > 0 && (
        <p className="text-xs text-muted-foreground">
          Accepting leaves {formatCurrency(leftover, { currency })} of this
          payment in the queue.
        </p>
      )}
    </div>
  );
}

// Spelled out once: these are the only explanation an icon gets, so they say
// what the action does and - for the way out - what it does not touch.
const ALLOCATE_TITLE =
  'Allocate — open the screen and set the amounts yourself';
const REJECT_TITLE =
  'Not a match — clears this suggestion. The payment stays in the queue.';

const PLACEABLE: IncomingMatchStatus[] = ['UNMATCHED', 'PARTIAL_MATCHED'];

/** What the suggestion would place, or null when it names no amounts. */
function suggestedAmount(line: BankIncomingAdminLine): number | null {
  if (line.suggested_allocations.length === 0) return null;
  return line.suggested_allocations.reduce(
    (total, allocation) => total + allocation.amount,
    0
  );
}
